import { useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { audioStateOf, playSrc, playWord, subscribeAudioState, type AudioState } from '@/lib/wordAudio';

/**
 * Кнопка озвучки — одна на всё приложение ученика.
 *
 * ⚠️ ЗАЧЕМ ОБЩАЯ. Кнопок озвучки было пять штук в пяти файлах, и состояние
 * загрузки было ровно у одной — у слова дня. В словаре, в наборе слов марафона,
 * в карточках и в «услышал и напечатал» кнопка выглядела одинаково и до, и во
 * время загрузки. Ученику с плохим интернетом это читается как «платформа
 * зависла»: нажал — тишина — нажал ещё раз — снова тишина.
 *
 * ⚠️ ПОЧЕМУ НЕ «СКАЧАТЬ, ПОТОМ ИГРАТЬ». Соблазнительно дождаться байтов и лишь
 * потом включить кнопку — тогда состояние известно точно. Так сделано у слова
 * дня, и там это уместно: карточка одна и висит заранее.
 *
 * Здесь так нельзя, и это записано кровью в `lib/wordAudio.ts`: пока идёт
 * ожидание сети, разрешение на звук, выданное браузером под нажатие пальцем,
 * ИСТЕКАЕТ — и проигрывание отказывает молча. Поэтому нажатие запускает звук
 * сразу, а показ состояния идёт следом, из событий элемента.
 */

const ICON: Record<AudioState, string> = {
  idle: 'volume_up',
  loading: 'progress_activity',
  playing: 'volume_up',
  error: 'volume_off',
};

type Base = {
  /** Показывать подпись рядом со значком (список словаря), а не только значок. */
  withLabel?: boolean;
  /** Крупная круглая кнопка — «услышал и напечатал», где она главная на экране. */
  big?: boolean;
  className?: string;
};

/**
 * Звук задаётся ОДНИМ из трёх способов:
 *
 *  • `word` — сервер синтезирует и запомнит (словарь, набор слов);
 *  • `src`  — готовая ссылка (карточки уже знают свой audio_url);
 *  • `state` + `onPlay` — состояние приходит снаружи.
 *
 * ⚠️ ТРЕТИЙ СПОСОБ — НЕ ЗАПАСНОЙ ВЫХОД, А СЛОВО ДНЯ. Там кнопка ждёт, пока
 * запись скачается ЦЕЛИКОМ (`hooks/useWordAudio`), и это требование заказчика
 * от 05.09.2026 — «сделать как в camp». Стратегию менять нельзя, а выглядеть
 * кнопка должна как все остальные. Поэтому вид общий, а откуда состояние —
 * дело места вызова.
 */
export type AudioPlayButtonProps = Base &
  (
    | { word: string; src?: never; state?: never; onPlay?: never }
    | { src: string; word?: never; state?: never; onPlay?: never }
    | { state: AudioState; onPlay: () => void; word?: never; src?: never }
  );

export function AudioPlayButton({
  word,
  src,
  state: given,
  onPlay,
  withLabel,
  big,
  className,
}: AudioPlayButtonProps) {
  const { t } = useTranslation();
  const key = word ?? src ?? '';
  const shared = useSyncExternalStore(
    subscribeAudioState,
    () => audioStateOf(key),
    () => 'idle' as AudioState,
  );
  const state = given ?? shared;

  const loading = state === 'loading';
  const failed = state === 'error';

  // ⚠️ ВЫКЛЮЧАТЬ ИЛИ НЕТ — ЗАВИСИТ ОТ СТРАТЕГИИ ЗАГРУЗКИ, И ЭТО НЕ ПРИДИРКА.
  //
  // На общем проигрывателе (word/src) звук стартует сразу, и нажатие во время
  // загрузки осмысленно: первое могло не дойти (браузер отказал в автозвуке),
  // второе обязано сработать. Выключенная серая кнопка там читается как
  // поломка.
  //
  // При своей стратегии (слово дня качает запись целиком) нажатие до конца
  // загрузки не делает НИЧЕГО — `useWordAudio.play` выходит на `!ready`. Вот
  // это и есть молчаливый отказ, на который заказчик жаловался 05.09.2026.
  // Поэтому здесь кнопка выключается, пока идёт загрузка.
  //
  // В ошибке кнопка живая всегда: она же и «повторить», отдельной кнопки нет.
  const disabled = Boolean(onPlay) && loading;
  const label = failed ? t('audio.retry') : loading ? t('audio.loading') : t('audio.play');

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => {
        if (onPlay) onPlay();
        else if (word) void playWord(word);
        else if (src) playSrc(src);
      }}
      aria-label={label}
      aria-busy={loading}
      title={label}
      className={cn(
        'inline-flex items-center gap-2 rounded-button font-display font-semibold',
        'text-on-surface transition hover:bg-surface-container-high/60 active:scale-[0.98]',
        disabled && 'opacity-50 cursor-progress',
        withLabel ? 'h-8 px-3 text-sm' : 'justify-center',
        !withLabel && (big ? 'h-16 w-16 rounded-full' : 'h-9 w-9'),
        failed && 'text-error',
        className,
      )}
    >
      <span
        className={cn(
          'material-symbols-outlined',
          big && 'text-3xl',
          loading && 'animate-spin',
          state === 'playing' && 'text-primary',
        )}
        style={state === 'playing' ? { fontVariationSettings: "'FILL' 1" } : undefined}
        aria-hidden="true"
      >
        {ICON[state]}
      </span>
      {withLabel && <span>{label}</span>}
    </button>
  );
}
